import { useState } from 'react'
import { AxiosError } from 'axios'
import { Box, Flex, Text } from '@radix-ui/themes'
import {
  IconChevronDown,
  IconChevronUp,
  IconAlertTriangleFilled,
  IconInfoCircle,
} from '@tabler/icons-react'
import { useQualifyMark } from '@/services/qualifyService'
import { Marking } from '@/types/attempt'
import { SheetData } from '@/types/question'
import { toast } from 'sonner'
// import { truncate } from '@/lib/utils'
import { getMarksPercentageStyle } from '@/utils/style'
import { Button } from '@/components/ui/button'
import Hstack from '@/components/ui/hstack'
import Stack from '@/components/ui/stack'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  ThumbDownIcon,
  ThumbUpIcon,
  FeedbackFillIcon,
  FeedbackIcon,
} from '@/components/svg/marking-icon'
import ComplexCSVDisplay from './ComplexCSVDisplay'

export interface ResultProps {
  sections?: ResultSection[]
  items?: ResultItem[]
  onDataRefresh?: () => void
}

export interface ResultSection {
  id: string
  title: string
  questions: ResultItem[]
}

export interface ResultItem {
  id?: string
  subQuestionId?: number
  title: string
  prompt: string
  type: 'objective' | 'subjective'
  questionType?: 'single_choice' | 'multiple_choice' | 'text'
  answerHtml?: string
  answerSheetUrl?: string
  sub_question_answer_sheet_link_data?: SheetData[]
  objectiveAnswer?: string[]
  objectiveOptions?: ObjectiveOption[]
  objectiveCorrect?: string[]
  objectiveExplanation?: string
  aiFeedback?: Feedback
  tutorFeedback?: Feedback
  // Case study grouping
  variant?: 'normal' | 'caseStudy' | 'multiSubHeader'
  children?: ResultItem[] // only when variant === 'caseStudy'
  marking?: Marking
  marks?: number // Total marks for this question
  isHeaderOnly?: boolean // For header cards that only show question_text
  // Styling hint: a single sub-question item inside a case study should keep white background
  isSingleSubInCaseStudy?: boolean
}

export interface ObjectiveOption {
  id: string
  label: string
}

export interface Feedback {
  evaluatedAt?: string
  marks?: number
  feedback?: string[]
}

export function Result(props: ResultProps) {
  const { sections, items, onDataRefresh } = props

  const hasSections = Boolean(sections && sections.length)
  const hasItems = Boolean(items && items.length)
  if (!hasSections && !hasItems) return null

  if (hasSections) {
    return (
      <Flex
        direction='column'
        gap='8'
        className='max-w-[1200px] min-w-[1200px]'
      >
        {sections!.map((section) => (
          <Box key={section.id} className='mb-6 w-full p-4 md:p-6'>
            <Flex align='center' justify='between' className='mb-4'>
              <Text className='text-[24px] !leading-0 text-[#002652]'>
                {section.title}
              </Text>
              {/* <Text size='2' color='gray'>
                {section.questions.length} Questions
              </Text> */}
            </Flex>

            <Stack gap={'16px'}>
              <Flex direction='column' gap='4'>
                {section.questions.map((item, idx) => {
                  if (
                    (item.variant === 'caseStudy' ||
                      item.variant === 'multiSubHeader') &&
                    item.children &&
                    item.children.length
                  ) {
                    return (
                      <CaseStudyGroup
                        key={item.id ?? `cs-${idx}`}
                        parent={item}
                        onDataRefresh={onDataRefresh}
                      />
                    )
                  }
                  return (
                    <QuestionResultCard
                      key={item.id ?? idx}
                      item={item}
                      onDataRefresh={onDataRefresh}
                    />
                  )
                })}
              </Flex>
            </Stack>
          </Box>
        ))}
      </Flex>
    )
  }

  return (
    <Flex direction='column' gap='4' className='bg-red-400'>
      {items!.map((item, idx) => (
        <QuestionResultCard
          key={item.id ?? idx}
          item={item}
          onDataRefresh={onDataRefresh}
        />
      ))}
    </Flex>
  )
}

function CaseStudyGroup({
  parent,
  onDataRefresh,
}: {
  parent: ResultItem
  onDataRefresh?: () => void
}) {
  function getTypeLabel(item: ResultItem): string {
    if (item.type === 'objective') {
      if (item.questionType === 'single_choice') return 'Single Choice'
      if (item.questionType === 'multiple_choice') return 'Multiple Choice'
      return 'Multiple Choice'
    }
    return 'Standard'
  }

  const groupTypeLabel = (() => {
    const children = parent.children || []
    if (!children.length) return null
    const labels = Array.from(new Set(children.map(getTypeLabel)))
    if (labels.length === 1) return labels[0]
    return 'Mixed'
  })()
  const showBadge = parent.variant === 'caseStudy'
  return (
    <Box className='mb-4 rounded-[12px] border bg-[#F6F7F9]'>
      <Flex direction='column' className='p-4 md:p-6' gap='4'>
        <Flex align='start' justify='between' className='gap-4'>
          <div className='flex-1'>
            <Text size='3' className='text-[#475467]'>
              {parent?.title}
            </Text>
            <Text
              as='p'
              size='2'
              color='gray'
              className='mt-1 leading-relaxed whitespace-pre-line text-[#002652]'
            >
              {/* {truncate(parent.prompt, 800)} */}
              {parent?.prompt}
            </Text>
          </div>
          <Flex align='center' gap='2' className='mb-2'>
            {groupTypeLabel && parent.variant !== 'caseStudy' && (
              <span className='inline-flex items-center rounded-full border bg-white px-2 py-0.5 text-[11px] text-[#666] md:text-xs'>
                {groupTypeLabel}
              </span>
            )}
            {showBadge && (
              <span className='inline-flex items-center rounded-full border bg-white px-2 py-0.5 text-[11px] text-[#666] md:text-xs'>
                Case Study
              </span>
            )}
          </Flex>
        </Flex>
        <div className='flex flex-col gap-4'>
          {parent.children!.map((child, idx) => {
            if (
              (child.variant === 'caseStudy' ||
                child.variant === 'multiSubHeader') &&
              child.children &&
              child.children.length
            ) {
              return (
                <NestedQuestionGroup
                  key={child.id ?? `nested-${idx}`}
                  parent={child}
                  onDataRefresh={onDataRefresh}
                />
              )
            }
            return (
              <QuestionResultCard
                key={child.id ?? idx}
                item={child}
                nested
                parentVariant={parent.variant}
                onDataRefresh={onDataRefresh}
              />
            )
          })}
        </div>
      </Flex>
    </Box>
  )
}

function NestedQuestionGroup({
  parent,
  onDataRefresh,
}: {
  parent: ResultItem
  onDataRefresh?: () => void
}) {
  function getTypeLabel(item: ResultItem): string {
    if (item.type === 'objective') {
      if (item.questionType === 'single_choice') return 'Single Choice'
      if (item.questionType === 'multiple_choice') return 'Multiple Choice'
      return 'Single/Multiple Choice'
    }
    return 'Standard'
  }

  const groupTypeLabel = (() => {
    const children = parent.children || []
    if (!children.length) return null
    const labels = Array.from(new Set(children.map(getTypeLabel)))
    if (labels.length === 1) return labels[0]
    return 'Mixed'
  })()

  return (
    <Box className='mb-4 rounded-[12px] border bg-white'>
      <Flex direction='column' className='p-4 md:p-6' gap='4'>
        <Flex align='start' justify='between' className='gap-4'>
          <div className='flex-1'>
            <Text size='3' className='text-[#475467]'>
              {parent.title}
            </Text>
            <Text
              as='p'
              size='2'
              color='gray'
              className='mt-1 leading-relaxed whitespace-pre-line text-[#002652]'
            >
              {parent.prompt}
            </Text>
          </div>
          {groupTypeLabel && (
            <span className='inline-flex items-center rounded-full border bg-white px-2 py-0.5 text-[11px] text-[#666] md:text-xs'>
              {groupTypeLabel}
            </span>
          )}
        </Flex>
        <div className='flex flex-col gap-4'>
          {parent.children!.map((child, idx) => (
            <QuestionResultCard
              key={child.id ?? idx}
              item={child}
              nested
              parentVariant={parent.variant}
              onDataRefresh={onDataRefresh}
            />
          ))}
        </div>
      </Flex>
    </Box>
  )
}

// Helper function to calculate marks for objective questions
const calculateObjectiveMarks = (item: ResultItem): number => {
  if (
    item.type !== 'objective' ||
    !item.objectiveAnswer ||
    !item.objectiveCorrect
  )
    return 0

  const selectedIds = item.objectiveAnswer
  const correctIds = item.objectiveCorrect

  // Check if user selected exactly the correct answers (no more, no less)
  const isAllCorrect =
    selectedIds.length > 0 &&
    selectedIds.length === correctIds.length &&
    selectedIds.every((id) => correctIds.includes(id)) &&
    correctIds.every((id) => selectedIds.includes(id))

  return isAllCorrect ? item.marks || 0 : 0
}

function QuestionResultCard({
  item,
  nested,
  parentVariant,
  onDataRefresh,
}: {
  item: ResultItem
  nested?: boolean
  parentVariant?: 'normal' | 'caseStudy' | 'multiSubHeader'
  onDataRefresh?: () => void
}) {
  const { qualifyMarkAsync, isQualifyingMark } = useQualifyMark()
  const [isAnswerOpen, setIsAnswerOpen] = useState(false)
  const [isAIFeedbackOpen, _setIsAIFeedbackOpen] = useState(false)
  const [isTutorFeedbackOpen, _setIsTutorFeedbackOpen] = useState(false)
  const [_tutorFeedback, _setTutorFeedback] = useState('')

  const handleToggleQualify = (id: number) => {
    qualifyMarkAsync({
      tutor_marking_id: String(id),
    })
      .then(() => {
        // Call the refresh callback to update the parent's data
        onDataRefresh?.()
      })
      .catch((error: AxiosError) => {
        console.log('qualify err', error)
        const errorMsg =
          (error.response?.data as { detail?: string })?.detail ||
          'Failed to upload file'
        toast.error(errorMsg, {
          duration: 3000,
          position: 'top-right',
        })
      })
  }

  // Render header-only card (for questions with multiple sub-questions)
  if (item.isHeaderOnly) {
    return (
      <Box
        className={
          nested
            ? item.isSingleSubInCaseStudy
              ? 'rounded-[12px] border bg-white'
              : 'rounded-[12px] border bg-[#F6F7F9]'
            : 'rounded-[12px] border bg-white'
        }
        mb={'16px'}
      >
        <Flex direction='column' className='p-4 md:p-6' gap='4'>
          <Flex align='start' justify='between' className='gap-4'>
            <div className='flex-1'>
              <Text size='3' className='text-[#475467]'>
                {item.title}
              </Text>
              <Text
                as='p'
                size='2'
                color='gray'
                className='mt-1 leading-relaxed whitespace-pre-line text-[#002652]'
              >
                {item.prompt}
              </Text>
            </div>
          </Flex>
        </Flex>
      </Box>
    )
  }

  return (
    <>
      <Box
        className={
          item.isSingleSubInCaseStudy || parentVariant === 'multiSubHeader'
            ? 'rounded-[12px] border bg-white'
            : 'rounded-[12px] border bg-[#F6F7F9]'
        }
        mb={'16px'}
      >
        <Flex direction='column' className='p-4 md:p-6' gap='4'>
          <Flex align='start' justify='between' className='gap-4'>
            <div className='flex-1'>
              <Text size='3' className='text-[#475467]'>
                {item.title}
              </Text>
              <Text
                as='p'
                size='2'
                color='gray'
                className='mt-1 leading-relaxed whitespace-pre-line text-[#002652]'
              >
                {/* {truncate(item.prompt, 500)} */}
                {item.prompt}
              </Text>
            </div>
            <Flex align='center' gap='2' className='mb-2'>
              {!(
                parentVariant === 'multiSubHeader' ||
                (parentVariant === 'caseStudy' &&
                  item.isSingleSubInCaseStudy === true)
              ) && (
                <span className='inline-flex items-center rounded-full border bg-white px-2 py-0.5 text-[11px] text-[#666] md:text-xs'>
                  {item.type === 'objective'
                    ? item.questionType === 'single_choice'
                      ? 'Single Choice'
                      : item.questionType === 'multiple_choice'
                        ? 'Multiple Choice'
                        : 'Single/Multiple Choice'
                    : 'Standard'}
                </span>
              )}
            </Flex>
            {/* <Flex direction="column" align="end" className="shrink-0 min-w-[110px]">
            {typeof item.aiFeedback?.marks === "number" && (
              <Text size="2" weight="medium">Marks: {item.aiFeedback.marks}/5</Text>
            )}
            {typeof item.tutorFeedback?.marks === "number" ? (
              <Text size="2" color="gray">Tutor: {item.tutorFeedback.marks}/5</Text>
            ) : (
              <Text size="2" color="gray">Tutor: -/5</Text>
            )}
          </Flex> */}
          </Flex>

          {/* Accordions: Answer & AI Feedback */}
          <div className='mb-4 rounded-[10px] border bg-white'>
            <button
              type='button'
              onClick={() => setIsAnswerOpen((v) => !v)}
              aria-expanded={isAnswerOpen}
              aria-controls={`answer-${item.id}`}
              className='flex w-full items-center justify-between gap-2 px-3 py-2'
            >
              <Hstack gapX={'2px'} align='center'>
                {isAnswerOpen ? <IconChevronUp /> : <IconChevronDown />}
                <span className='text-sm font-medium text-[#0E2245]'>
                  Answer
                </span>
              </Hstack>
              {item.type === 'objective' && (
                <Hstack
                  justify='end'
                  className={`rounded-sm border px-2 py-1 ${getMarksPercentageStyle(calculateObjectiveMarks(item), item.marks ?? 0)}`}
                >
                  <Text size='2' className='text-white'>
                    Marks: {calculateObjectiveMarks(item)}/{item.marks || 0}
                  </Text>
                </Hstack>
              )}
            </button>
            {isAnswerOpen && (
              <Box id={`answer-${item.id}`} className='border-t px-3 py-3'>
                {item.type === 'subjective' ? (
                  <>
                    {item?.answerHtml ? (
                      <div
                        className='text-foreground [&_*]:!text-foreground max-w-none text-sm leading-relaxed whitespace-pre-line'
                        dangerouslySetInnerHTML={{ __html: item.answerHtml }}
                      />
                    ) : (
                      <Text size='2' className='text-white'>
                        No answer available
                      </Text>
                    )}
                    {item.sub_question_answer_sheet_link_data &&
                      item.sub_question_answer_sheet_link_data.length > 0 && (
                        // <div className="mt-4">
                        //   <iframe
                        //     src={item.answerSheetUrl}
                        //     title="Answer sheet"
                        //     className="h-[480px] w-full rounded-md border"
                        //     allow="fullscreen"
                        //   />
                        // </div>
                        <>
                          {/* Complex CSV Data Display */}
                          <ComplexCSVDisplay
                            sheetData={
                              item.sub_question_answer_sheet_link_data ?? []
                            }
                          />
                        </>
                      )}
                  </>
                ) : (
                  <ObjectiveAnswerDetail
                    selectedIds={item.objectiveAnswer ?? []}
                    correctIds={item.objectiveCorrect ?? []}
                    options={item.objectiveOptions ?? []}
                    explanation={item.objectiveExplanation}
                  />
                )}
              </Box>
            )}
          </div>

          {/* Only show AI Feedback for text questions */}
          {item.type === 'subjective' && (
            <div className='mb-4 rounded-[10px] border bg-white'>
              <button
                type='button'
                onClick={() => _setIsAIFeedbackOpen((v) => !v)}
                aria-expanded={isAIFeedbackOpen}
                aria-controls={`feedback-${item.id}`}
                className='flex w-full items-center justify-between gap-2 px-3 py-2'
              >
                <Hstack gapX={'2px'} align='center'>
                  {isAIFeedbackOpen ? <IconChevronUp /> : <IconChevronDown />}
                  <span className='text-sm font-medium text-[#0E2245]'>
                    AI Feedback
                  </span>
                </Hstack>
                {item.questionType === 'text' && (
                  <Hstack justify='end' gapX={'8px'} align='center'>
                    {item?.marking?.ai_marking?.student_like === true ? (
                      <Hstack>
                        <ThumbUpIcon size={24} />
                        <ThumbDownIcon size={24} />
                      </Hstack>
                    ) : item?.marking?.ai_marking?.student_like === false ? (
                      <Hstack>
                        <ThumbUpIcon size={24} cl='white' />
                        <ThumbUpIcon
                          size={24}
                          style={{ transform: 'rotate(180deg)' }}
                        />
                      </Hstack>
                    ) : (
                      <Hstack>
                        <ThumbUpIcon size={24} cl='white' />
                        <ThumbDownIcon size={24} />
                      </Hstack>
                    )}
                    {!item?.marking?.ai_marking?.student_comment ? (
                      <FeedbackIcon size={24} />
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type='button'
                            aria-label='Assessment info'
                            className='rounded-full p-1 text-gray-400 hover:text-gray-600 focus:ring-1 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden'
                            tabIndex={0}
                          >
                            <FeedbackFillIcon size={24} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side='right'
                          sideOffset={8}
                          hideArrow
                          className='max-w-[340px] rounded-[20px] bg-[#D9D9D9] px-4 py-3 text-[12px] text-[#404040] shadow-none'
                        >
                          <Stack gap={'0px'}>
                            <Text>
                              {item?.marking?.ai_marking?.student_comment}
                            </Text>
                          </Stack>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {item?.marking !== null && (
                      <div
                        className={`rounded-sm border px-2 py-1 ${getMarksPercentageStyle(item?.marking?.ai_marking?.mark ?? 0, item.marks ?? 0)}`}
                      >
                        <Text size='2' className='text-white'>
                          Marks: {item?.marking?.ai_marking?.mark || 0}/
                          {item.marks || 0}
                        </Text>
                      </div>
                    )}
                  </Hstack>
                )}
              </button>
              {isAIFeedbackOpen && (
                <Box
                  id={`feedback-${item.id}`}
                  className='border-t px-3 py-3 whitespace-pre-line'
                >
                  {item?.marking?.ai_marking?.feedback ? (
                    <Text>{item?.marking?.ai_marking?.feedback}</Text>
                  ) : (
                    <Text>No feedback yet</Text>
                  )}
                </Box>
              )}
            </div>
          )}

          {/* Only show Tutor Feedback for text questions */}
          {item.type === 'subjective' && (
            <div className='rounded-[10px] border bg-white'>
              <button
                type='button'
                onClick={() => _setIsTutorFeedbackOpen((v) => !v)}
                aria-expanded={isTutorFeedbackOpen}
                aria-controls={`feedback-${item.id}`}
                className='flex w-full items-center justify-between gap-2 px-3 py-2'
              >
                <Hstack gapX={'2px'} align='center'>
                  {isTutorFeedbackOpen ? (
                    <IconChevronUp />
                  ) : (
                    <IconChevronDown />
                  )}
                  <span className='text-sm font-medium text-[#0E2245]'>
                    Tutor Feedback
                  </span>
                </Hstack>
                {item.questionType === 'text' && (
                  <Hstack
                    justify='end'
                    align={'center'}
                    gapX={'4px'}
                    className='relative z-50'
                  >
                    <>
                      <Button
                        className='relative z-50'
                        disabled={
                          isQualifyingMark || !item?.marking?.tutor_marking?.id
                        }
                        onClick={(e) => {
                          e.stopPropagation()
                          handleToggleQualify(
                            item?.marking?.tutor_marking?.id ?? 0
                          )
                        }}
                      >
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className='inline-flex items-center'
                                tabIndex={0}
                                role='img'
                                aria-label='Qualify info'
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <IconInfoCircle className='relative left-0 z-50 size-5' />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent
                              side='top'
                              sideOffset={6}
                              className='z-[9999] max-w-[290px] bg-[#D9D9D9] text-[#404040]'
                              hideArrow
                            >
                              {item?.marking?.tutor_marking?.is_qualified ? (
                                <div className='text-center'>
                                  <p className='text-base font-semibold'>
                                    Disqualify
                                  </p>
                                  <p className='text-sm'>
                                    If disqualified, Students will no longer see
                                    this feedback. Tutor will still see this
                                    feedback mared as <b>“Disqualified”.</b>
                                  </p>
                                </div>
                              ) : (
                                <div className='text-center'>
                                  <p className='text-base font-semibold'>
                                    Qualify
                                  </p>
                                  <p className='text-sm'>
                                    If qualified, Students will see this
                                    feedback.
                                  </p>
                                </div>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        {item?.marking?.tutor_marking?.is_qualified
                          ? 'Disqualify'
                          : 'Qualify'}
                      </Button>
                      {!item?.marking?.tutor_marking?.is_qualified && (
                        <Hstack gapX={'2px'} align='center'>
                          <IconAlertTriangleFilled size={24} />
                        </Hstack>
                      )}
                    </>

                    {!item?.marking?.tutor_marking?.student_comment ? (
                      <FeedbackIcon size={24} />
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type='button'
                            aria-label='Assessment info'
                            className='rounded-full p-1 text-gray-400 hover:text-gray-600 focus:ring-1 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden'
                            tabIndex={0}
                          >
                            <FeedbackFillIcon size={24} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side='right'
                          sideOffset={8}
                          hideArrow
                          className='max-w-[340px] rounded-[20px] bg-[#D9D9D9] px-4 py-3 text-[12px] text-[#404040] shadow-none'
                        >
                          <Stack gap={'0px'}>
                            <Text>
                              {item?.marking?.tutor_marking?.student_comment}
                            </Text>
                          </Stack>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {item?.marking !== null && (
                      <div
                        className={`rounded-sm border px-2 py-1 ${getMarksPercentageStyle(item?.marking?.tutor_marking?.mark ?? 0, item.marks ?? 0)}`}
                      >
                        <Text size='2' className='text-white'>
                          Marks: {item?.marking?.tutor_marking?.mark || 0}/
                          {item.marks || 0}
                        </Text>
                      </div>
                    )}
                  </Hstack>
                )}
              </button>

              {isTutorFeedbackOpen && (
                <Box
                  id={`feedback-${item.id}`}
                  className='border-t px-3 py-3 whitespace-pre-line'
                >
                  {item?.marking?.tutor_marking?.feedback ? (
                    <Text>{item?.marking?.tutor_marking?.feedback}</Text>
                  ) : (
                    <Text>No feedback yet</Text>
                  )}
                  {/* <TutorFeedbackSection
                  tutorFeedback={tutorFeedback}
                  setTutorFeedback={setTutorFeedback}
                /> */}
                </Box>
              )}
            </div>
          )}
        </Flex>
      </Box>
    </>
  )
}

// function FeedbackSection(props: {
//   label: string;
//   date?: string;
//   children: React.ReactNode;
// }) {
//   const { label, date, children } = props;
//   return (
//     <section>
//       <Flex align="center" justify="between" className="mb-2">
//         <Heading size="2" as="h4">
//           {label}
//         </Heading>
//         {date && (
//           <Text size="1" color="gray">
//             {date}
//           </Text>
//         )}
//       </Flex>
//       {children}
//     </section>
//   );
// }

function ObjectiveAnswerDetail({
  selectedIds,
  correctIds,
  options,
  explanation,
}: {
  selectedIds: string[]
  correctIds: string[]
  options: ObjectiveOption[]
  explanation?: string
}) {
  const labelById = new Map(options.map((o) => [o.id, o.label]))
  const yourLabels = selectedIds.map((id) => labelById.get(id) ?? id)
  const correctLabels = correctIds.map((id) => labelById.get(id) ?? id)
  // Check if user selected exactly the correct answers (no more, no less)
  const isAllCorrect =
    selectedIds.length > 0 &&
    selectedIds.length === correctIds.length &&
    selectedIds.every((id) => correctIds.includes(id)) &&
    correctIds.every((id) => selectedIds.includes(id))

  return (
    <div>
      <div className='mb-2 rounded border bg-[#F6F7F9] px-3 py-2 text-sm'>
        <div className='flex items-start justify-between gap-2'>
          <div className='flex-1'>
            <div className='mb-2 flex items-center justify-between gap-2'>
              <div>
                <Text>Your Answer{yourLabels.length > 1 ? 's' : ''}:</Text>
              </div>
              <span
                className={`rounded px-2 py-0.5 text-xs ${
                  isAllCorrect
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {isAllCorrect ? 'Correct' : 'Incorrect'}
              </span>
            </div>
            {yourLabels.length > 0 ? (
              <div className='space-y-2'>
                {yourLabels.map((label, index) => {
                  const selectedId = selectedIds[index]
                  const isCorrect = correctIds.includes(selectedId)
                  return (
                    <div
                      key={index}
                      className='flex items-center justify-between gap-2 rounded border bg-white p-2'
                    >
                      <div className='flex items-center gap-2'>
                        {/* {isCorrect ? (
                          <div className='flex h-4 min-h-4 w-4 min-w-4 items-center justify-center rounded-full bg-green-500'>
                            <span className='text-xs text-white'>✓</span>
                          </div>
                        ) : (
                          <div className='flex h-4 min-h-4 w-4 min-w-4 items-center justify-center rounded-full bg-red-500'>
                            <span className='text-xs text-white'>✗</span>
                          </div>
                        )} */}
                        <span className='text-sm'>{label}</span>
                      </div>
                      <span
                        className={`rounded px-2 py-0.5 text-xs ${
                          isCorrect
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {isCorrect ? 'Correct' : 'Incorrect'}
                      </span>
                    </div>
                  )
                })}
              </div>
            ) : (
              <Text color='gray'>—</Text>
            )}
          </div>
        </div>
      </div>

      <div className='mb-2 rounded border bg-[#F6F7F9] px-3 py-2 text-sm'>
        <div className='flex items-center justify-between gap-2'>
          <Text>Correct Answer{correctLabels.length > 1 ? 's' : ''}:</Text>
        </div>

        {correctLabels.length > 0 &&
          correctLabels.map((label) => {
            return (
              <div className='mb-2 flex justify-between gap-2 rounded border bg-white p-2'>
                <div className='flex gap-2'>
                  <Text>{label}</Text>
                </div>
                <span
                  className={`rounded bg-green-100 px-2 py-0.5 text-xs text-green-700`}
                >
                  Correct
                </span>
              </div>
            )
          })}
      </div>

      {/* <div className="mb-3">
        <Text weight="medium" size="2">
          All Options
        </Text>
        <ul className="mt-1 divide-y rounded border text-sm bg-[#F6F7F9]">
          {options.map((opt) => {
            const isSelected = selectedIds.includes(opt.id);
            const isCorrect = correctIds.includes(opt.id);
            return (
              <li
                key={opt.id}
                className={`flex items-center justify-between gap-3 px-3 py-2 ${
                  isCorrect ? "bg-green-50" : ""
                }`}
              >
                <span className="flex items-center gap-2">
                  {isCorrect ? (
                    <IconCircleCheck className="h-4 w-4 text-green-600" />
                  ) : isSelected ? (
                    <IconCircleX className="h-4 w-4 text-red-600" />
                  ) : (
                    <span className="h-4 w-4 rounded-full border" />
                  )}
                  {opt.label}
                </span>
                <span
                  className={`text-xs ${
                    isCorrect
                      ? "text-green-700"
                      : isSelected
                      ? "text-red-700"
                      : "text-muted-foreground"
                  }`}
                >
                  {isCorrect ? "Correct" : isSelected ? "Your choice" : ""}
                </span>
              </li>
            );
          })}
        </ul>
      </div> */}

      {explanation && (
        <div className='rounded border px-3 py-2 text-sm'>
          <Text weight='medium'>Explanation:</Text>{' '}
          <Text color='gray'>{explanation}</Text>
        </div>
      )}
    </div>
  )
}

export default Result
